'use client';

import { ProductImage, ReviewData } from '@/models/Product';
import { useCallback, useEffect, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import MagnifyImage from './MagnifyImage';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Ratings } from './ui/stars';
import ProductResponsiveImage from './ProductResponsiveImage';
import FavoriteButton from './FavoriteButton';
import { PRODUCT_THUMBNAIL_WIDTHS } from '@/utils/cdnResponsiveImage';

export function galleryImageAlt(
  imageAlt: string | null | undefined,
  productName: string | undefined,
  index: number,
): string {
  const cmsAlt = imageAlt?.trim();
  if (cmsAlt) return cmsAlt;

  const name = productName?.trim() || 'Ürün';
  return index === 0 ? name : `${name} - ${index + 1}`;
}

export default function ProductImageGallery({
  images,
  reviewData,
  productId,
  productTitle,
  productUrl,
  productImageUrl,
}: {
  images: ProductImage[];
  reviewData: ReviewData | undefined;
  productId: number;
  productTitle?: string;
  productUrl?: string;
  productImageUrl?: string;
}) {
  const [selectedPage, setSelectedPage] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | undefined>(undefined);

  const onSelect = useCallback(() => {
    if (!carouselApi) return;
    const newIndex = carouselApi.selectedScrollSnap();
    setSelectedPage(newIndex);
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    carouselApi.on('select', onSelect);
    carouselApi.on('init', onSelect);
    carouselApi.on('reInit', onSelect);
    onSelect();

    return () => {
      carouselApi.off('select', onSelect);
      carouselApi.off('init', onSelect);
      carouselApi.off('reInit', onSelect);
    };
  }, [carouselApi, onSelect]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (carouselApi) {
        carouselApi.scrollTo(index);
        setTimeout(() => {
          setSelectedPage(index);
        }, 100);
      } else {
        setSelectedPage(index);
      }
    },
    [carouselApi],
  );

  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <ScrollArea className="order-2 sm:order-1 w-full sm:w-24 h-[100px] sm:h-auto">
        <div className="flex flex-row sm:flex-col gap-2 pb-2 sm:pb-0">
          {images.map((image, index) => (
            <div key={`thumb-${index}`} className="flex-shrink-0 p-0.5">
              <ProductResponsiveImage
                width={80}
                height={80}
                src={image.imageUrl}
                alt={galleryImageAlt(image.alt, productTitle, index)}
                widths={PRODUCT_THUMBNAIL_WIDTHS}
                fallbackWidth={100}
                sizes="80px"
                loading="lazy"
                className={`cursor-pointer rounded-lg w-[80px] h-[80px] object-cover border-2 transition-all duration-200 hover:scale-105 ${
                  index === selectedPage
                    ? 'border-blue-500 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleThumbnailClick(index)}
              />
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="sm:orientation-vertical" />
      </ScrollArea>

      <div className="order-1 sm:order-2 flex-1 relative">
        {reviewData && (
          <div className="flex lg:hidden flex-row items-center self-end absolute -right-4 -bottom-2 z-10 bg-white rounded-full p-2 border-2 border-[#EDEDF0] gap-2">
            <Ratings
              rating={reviewData.averageRating}
              onRatingChange={(_) => {}}
              variant="yellow"
              disabled={true}
              className="w-[96px]"
            />
            <span className="text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#5C5C57] underline">
              ({reviewData.reviewCount})
            </span>
          </div>
        )}

        <FavoriteButton
          productId={productId}
          productTitle={productTitle}
          productUrl={productUrl}
          productImageUrl={productImageUrl}
          size="md"
          variant="overlay"
          className="absolute top-3 right-3 z-10"
        />

        <Carousel
          key={`carousel-${images.length}`}
          className="border-2 border-[#EDEDF0] w-full rounded-xl overflow-hidden"
          opts={{
            align: 'start',
            loop: true,
            startIndex: selectedPage,
          }}
          plugins={[
            Autoplay({
              delay: 10_000,
              stopOnInteraction: true,
            }),
          ]}
          setApi={setCarouselApi}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={`slide-${index}`}>
                <div className="relative aspect-square w-full">
                  <MagnifyImage
                    src={image.imageUrl}
                    alt={galleryImageAlt(image.alt, productTitle, index)}
                    isLcp={index === 0}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}
